import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth, AUTH_PHASES } from "../auth/AuthProvider";
import { fetchEntitlement } from "../billing/billingApiClient";
import { fetchProfile } from "./accountApiClient";
import { adaptEntitlement } from "./entitlementAdapter";
import { captureScope, isCurrentScope } from "../auth/authSession";

const AccountContext = createContext(null);

export function AccountProvider({ children }) {
  const auth = useAuth();
  const [profile, setProfile] = useState(null);
  const [profileState, setProfileState] = useState("idle");
  const [profileError, setProfileError] = useState(null);
  const [entitlement, setEntitlement] = useState(null);
  const [entitlementState, setEntitlementState] = useState("idle");

  const loadProfile = useCallback(async () => {
    if (auth.phase !== AUTH_PHASES.active || !auth.uid) {
      return;
    }
    const scope = captureScope();
    setProfileState("loading");
    setProfileError(null);
    try {
      const json = await fetchProfile({ scope });
      if (!isCurrentScope(scope)) return;
      setProfile(json.profile);
      setProfileState("ready");
    } catch (err) {
      if (!isCurrentScope(scope)) return;
      setProfileError(err);
      setProfileState("error");
    }
  }, [auth.phase, auth.uid]);

  const loadEntitlement = useCallback(async (options = {}) => {
    if (auth.phase !== AUTH_PHASES.active || !auth.uid) {
      return;
    }
    const scope = captureScope();
    setEntitlementState("loading");
    try {
      const json = await fetchEntitlement({ scope, reconcile:options.reconcile === true });
      if (!isCurrentScope(scope)) return;
      setEntitlement((prev) =>
        adaptEntitlement(
          { status: json.status, entitlement: json.entitlement, uxGates: json.uxGates, correlationId: json.correlationId },
          { previous: prev, ownerUid: auth.uid }
        )
      );
      setEntitlementState("ready");
    } catch (err) {
      if (!isCurrentScope(scope)) return;
      setEntitlement((prev) =>
        adaptEntitlement(null, { previous: prev, ownerUid: auth.uid })
      );
      setEntitlementState("error");
      void err;
    }
  }, [auth.phase, auth.uid]);

  useEffect(() => {
    setProfile(null);
    setEntitlement(null);
    setProfileState("idle");
    setEntitlementState("idle");
    if (auth.phase === AUTH_PHASES.active) {
      loadProfile();
      loadEntitlement();
    }
  }, [auth.uid, auth.phase, loadProfile, loadEntitlement]);

  const value = useMemo(
    () => ({
      profile,
      profileState,
      profileError,
      entitlement,
      entitlementState,
      reloadProfile: loadProfile,
      reloadEntitlement: () => loadEntitlement({reconcile:true}),
    }),
    [profile, profileState, profileError, entitlement, entitlementState, loadProfile, loadEntitlement]
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount() {
  const ctx = useContext(AccountContext);
  if (!ctx) throw new Error("useAccount must be used inside AccountProvider");
  return ctx;
}
